import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Building2, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { api, downloadPdf, getActiveStructure, setActiveStructure } from "@/api";
import { useAuth } from "@/auth";
import ClientDashboardView from "@/components/ClientDashboardView";

function StructurePicker({ structures, onPick, onLogout }) {
  return (
    <div className="theme-brand min-h-screen flex items-center justify-center px-5" style={{ background: "#12100E" }} data-testid="structure-picker">
      <div className="w-full max-w-md">
        <div className="font-display text-lg mb-8" style={{ color: "#FAF7F2" }}>YU <span style={{ color: "#E05A2C" }}>YieldUp</span></div>
        <h1 className="font-display text-3xl" style={{ color: "#FAF7F2" }}>Quale struttura vuoi vedere?</h1>
        <p className="mt-2 text-sm" style={{ color: "#857C72" }}>Potrai cambiarla in qualsiasi momento dal menu in alto.</p>
        <div className="mt-8 space-y-3">
          {structures.map((s) => (
            <button key={s.id} onClick={() => onPick(s.id)} data-testid={`pick-structure-${s.id}`}
              className="w-full flex items-center gap-4 rounded-2xl px-5 py-4 text-left transition-colors hover:border-[#E05A2C]"
              style={{ background: "#1C1815", border: "1px solid #2A241F", color: "#FAF7F2" }}>
              <Building2 className="h-5 w-5" style={{ color: "#E05A2C" }} />
              <span className="flex-1 font-medium">{s.name}</span>
              <ChevronRight className="h-4 w-4" style={{ color: "#857C72" }} />
            </button>
          ))}
        </div>
        <button onClick={onLogout} className="mt-8 text-sm inline-flex items-center gap-2" style={{ color: "#857C72" }} data-testid="picker-logout"><LogOut className="h-4 w-4" /> Esci</button>
      </div>
    </div>
  );
}

export default function ClientDashboard() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [structures, setStructures] = useState(null);
  const [active, setActive] = useState(getActiveStructure());

  useEffect(() => {
    api.me().then((u) => setStructures(u.structures || [])).catch(() => setStructures([]));
  }, []);

  const pick = (id) => { setActiveStructure(id); setActive(id); };
  const doLogout = () => { logout(); navigate("/login", { replace: true }); };

  if (structures === null) return <div className="theme-brand min-h-screen" style={{ background: "#12100E" }} />;
  if (structures.length > 1 && !structures.some((s) => s.id === active)) {
    return <StructurePicker structures={structures} onPick={pick} onLogout={doLogout} />;
  }
  return <ClientDashboardInner key={active || "default"} structures={structures} active={active} onSwitch={pick} />;
}

function ClientDashboardInner({ structures, active, onSwitch }) {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [overview, monthly, yearly, messages, pricing, opportunities] = await Promise.all([
        api.myOverview(),
        api.myPeriods("monthly"),
        api.myPeriods("yearly"),
        api.myMessages(),
        api.myPricing().catch(() => ({ strategies: [] })),
        api.myOpportunities().catch(() => ({ years: [] })),
      ]);
      setData({ overview, monthly, yearly, messages, pricing: pricing?.strategies || [], opportunities: opportunities?.years || [] });
    } catch (e) {
      setError(e.message || "Errore di caricamento");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onSendMessage = async (text) => {
    try {
      await api.sendMessage(text);
      const messages = await api.myMessages();
      setData((d) => ({ ...d, messages }));
      toast.success("Segnalazione inviata");
    } catch (e) {
      toast.error(e.message || "Invio non riuscito");
    }
  };

  const onReplyMessage = async (mid, text) => {
    try {
      await api.replyMessage(mid, text);
      const messages = await api.myMessages();
      setData((d) => ({ ...d, messages }));
      toast.success("Risposta inviata");
    } catch (e) {
      toast.error(e.message || "Invio non riuscito");
    }
  };

  const onDownloadPdf = async (period) => {
    try {
      await downloadPdf(`/me/periods/${period.id}/report.pdf`, `YieldUp-${period.period_key}.pdf`);
    } catch (e) {
      toast.error("Download del report non riuscito");
    }
  };

  const onDownloadPricingPdf = async (year) => {
    try {
      await downloadPdf(`/me/pricing/${year}/pdf`, `YieldUp-Strategia-${year}.pdf`);
    } catch (e) {
      toast.error("Download della strategia non riuscito");
    }
  };

  const onDownloadOpportunitiesPdf = async (year) => {
    try {
      await downloadPdf(`/me/opportunities/${year}/pdf`, `YieldUp-Opportunita-${year}.pdf`);
    } catch (e) {
      toast.error("Download delle opportunità non riuscito");
    }
  };

  const onRefreshAir = async (year) => {
    try {
      await api.myAirRefresh(year);
      const opportunities = await api.myOpportunities().catch(() => ({ years: [] }));
      setData((d) => ({ ...d, opportunities: opportunities?.years || [] }));
      toast.success("Check voli aggiornato");
    } catch (e) {
      toast.error(e.message || "Aggiornamento voli non riuscito");
    }
  };

  const onPricingFeedback = async (year, state, message) => {
    try {
      await api.pricingFeedback(year, { state, message: message || "" });
      const pricing = await api.myPricing().catch(() => ({ strategies: [] }));
      setData((d) => ({ ...d, pricing: pricing?.strategies || [] }));
      toast.success(state === "confirmed" ? "Strategia confermata, grazie!" : "Richiesta inviata al tuo specialista");
    } catch (e) {
      toast.error(e.message || "Invio non riuscito");
    }
  };

  const doLogout = () => { logout(); navigate("/login", { replace: true }); };

  return (
    <div className="theme-brand min-h-screen" style={{ background: "#12100E" }}>
      <header className="sticky top-0 z-30 backdrop-blur-md" style={{ background: "rgba(18,16,14,0.85)", borderBottom: "1px solid #1C1815" }}>
        <div className="max-w-5xl mx-auto px-5 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <div className="font-display text-lg shrink-0" style={{ color: "#FAF7F2" }}>YU <span style={{ color: "#E05A2C" }}>YieldUp</span></div>
            {structures.length > 1 ? (
              <select value={active} onChange={(e) => onSwitch(e.target.value)} data-testid="structure-switcher"
                className="min-w-0 max-w-[150px] sm:max-w-xs truncate rounded-full px-3 py-1.5 text-sm outline-none"
                style={{ background: "#1C1815", border: "1px solid #2A241F", color: "#FAF7F2" }}>
                {structures.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            ) : null}
          </div>
          <button onClick={doLogout} data-testid="client-logout" className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm"
            style={{ border: "1px solid #2A241F", color: "#C4BBB0" }}>
            <LogOut className="h-4 w-4" /> Esci
          </button>
        </div>
      </header>

      {loading ? (
        <div className="max-w-5xl mx-auto px-6 py-20 eyebrow" style={{ color: "#857C72" }}>Caricamento della dashboard…</div>
      ) : error ? (
        <div className="max-w-5xl mx-auto px-6 py-20" style={{ color: "#C4BBB0" }}>{error}</div>
      ) : (
        <ClientDashboardView
          overview={data.overview}
          monthly={data.monthly}
          yearly={data.yearly}
          messages={data.messages}
          pricing={data.pricing}
          opportunities={data.opportunities}
          onSendMessage={onSendMessage}
          onReplyMessage={onReplyMessage}
          onDownloadPdf={onDownloadPdf}
          onDownloadPricingPdf={onDownloadPricingPdf}
          onDownloadOpportunitiesPdf={onDownloadOpportunitiesPdf}
          onPricingFeedback={onPricingFeedback}
          onRefreshAir={onRefreshAir}
        />
      )}
    </div>
  );
}
